import React from 'react';
import _ from 'lodash';
import cx from 'classnames';

// material-ui imports
import { withStyles } from '@material-ui/core/styles';
import { Theme } from '@material-ui/core/styles/createMuiTheme';
import {
  createStyles,
  WithStyles,
  InputLabel,
  InputBase,
  FormHelperText,
  NativeSelect,
  Chip,
  Typography,
  FormControl
} from '@material-ui/core';
import Select from '@material-ui/core/Select';
import { Error } from '@material-ui/icons';
import { fade } from '@material-ui/core/styles/colorManipulator';
import { SectionFocusedContext, SectionFocusedContextValue } from './MyPATBaseSection';

// styles
const styles = createStyles((theme: Theme) => ({
  formControl: {
    margin: theme.spacing.unit,
    minWidth: 120,
    maxWidth: 300,
  },
  selectControl: {
    margin: 0,
    border: 0,
    display: 'inline-flex',
    padding: 0,
    position: 'relative',
    flexDirection: 'column',
    verticalAlign: 'top',
    '& *:after': {
      border: 'none !important'
    },
    '& *:before': {
      border: 'none !important'
    }
  },
  inputRoot: {
    '&>svg': {
      opacity: 0
    },
    'label + &': {
      marginTop: theme.spacing.unit * 3,
    },
    // '&>:first-child': {
    //   color: "hsl(0deg 0% 65%)"
    // }
  },
  input: {
    "-webkit-appearance": "none",
    background: "url(/assets/svg/dropdown-arrow.svg) 0 0 no-repeat",
    backgroundPositionX: "96%",
    backgroundPositionY: "center",
    borderRadius: theme.shape.borderRadius,
    position: 'relative',
    backgroundColor: 'white',
    border: '1px solid #dfdfdf',
    fontSize: 16,
    padding: [[theme.spacing.unit, theme.spacing.unit * 2]],
    minWidth: theme.spacing.unit * 3,
    transition: theme.transitions.create(['border-color', 'box-shadow']),
    paddingRight: 40,
    boxSizing: "border-box",
    height: 38,
    width: "100%",
    '&:focus': {
      borderColor: '#a1a1a1',
      borderRadius: theme.shape.borderRadius
    }
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  chip: {
    margin: 2,
  },
  inputFormLabel: {
    fontSize: 18,
    top: 0,
    left: 0,
    position: 'absolute',
    color: 'rgb(51, 51, 51)',
    "& > span": {
      color: '#F21D1D',
    }
  },
  formControlError: {
    '&  select': {
      background: fade(theme.palette.error.light, 0.1),
    },
    '& > label': {
      color: '#F21D1D',
    },
    '& >  p': {
      color: '#F21D1D',
    },
  },
  errorText: {
    color: '#F21D1D',
    verticalAlign: 'middle',
    marginTop: '3px',
    '& svg': {
      verticalAlign: 'bottom',
      marginRight: '5px',
    },
  },
  disabled:{
    cursor: 'not-allowed !important',
    opacity: 0.5
  },
  errorBorder:{
    border: '1px solid #F21D1D',
    borderRadius: '4px',
  },
  menuPaper: {
    maxHeight: 280,
    maxWidth: 431,
    "& li":{
      whiteSpace: 'initial',
    },
  }
}));

interface MyPATSelectProps extends WithStyles<typeof styles> {
  children: any,
  fullWidthControl: boolean,
  fullWidthInput: boolean,
  selectMinWidth: boolean,
  label: string,
  inputId: string,
  helperText: string,
  required: boolean,
  multiple: boolean,
  classes: any,
  isNative: boolean,
  error: boolean,
  value?: string,
  errorText: string,
  placeholder?: JSX.Element | string;
  inputClass?: any;
  onFocus: (e: any) => void;
  onBlur: (e: any) => void;
  customRenderFunc: (selected: any) => void
}

const MyPATSelect = (props: MyPATSelectProps) => {
  const {
    classes,
    fullWidthControl = false,
    fullWidthInput = false,
    isNative = true,
    selectMinWidth = true,
    label,
    inputId,
    helperText,
    children,
    required,
    error,
    errorText,
    placeholder,
    customRenderFunc,
    multiple,
    inputClass = '',
    onFocus,
    onBlur,
    ...inputProps
  } = props;

  const getRenderValue = (selected: any) => {
    let data: any = !multiple ? <em>Select</em> : '';
    if (selected && selected.length > 0) {
      if (!_.isUndefined(customRenderFunc)) {
        data = customRenderFunc(selected);
      } else {
        data = (Array.isArray(selected) && selected.join(', ')) || selected;
      }
    } else if (placeholder) {
      data = placeholder;
    }
    return data;
  }

  return (
    <SectionFocusedContext.Consumer>
      {({ setSectionFocus }: SectionFocusedContextValue) => (

      <div className={cx(classes.selectControl, (error ? classes.formControlError : ""))}
        style={{
          width: fullWidthControl && '100%',
          minWidth: selectMinWidth && '150px'
        }}
      >
        {_.isEmpty(label) || (
          <InputLabel shrink htmlFor={inputId} className={classes.inputFormLabel} required={required}>
            {label}
          </InputLabel>
        )}
        {isNative
          ? (
            <NativeSelect
              id={inputId}
              classes={{
                root: classes.inputRoot,
                select: [classes.input, inputClass].join(' '),
                disabled: classes.disabled
              }}
              fullWidth={fullWidthInput}
              required={required}
              input={
                <InputBase
                  classes={{
                    root: classes.inputRoot,
                  }}
                />
              }
              onFocus={(e: any) => {
                onFocus && onFocus(e);
                setSectionFocus(true)
              }}
              onBlur={(e: any) => {
                onBlur && onBlur(e);
                setSectionFocus(false)
              }}
              {...inputProps}
            >
              {children}
            </NativeSelect>
          ) : (
            <Select
              id={inputId}
              classes={{
                root: classes.inputRoot,
                select: [classes.input, inputClass].join(' '),
              }}
              fullWidth={fullWidthInput}
              required={required}
              className={error ? classes.errorBorder: ''}
              MenuProps={{ classes: { paper: classes.menuPaper }, anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left'
              }, 
              transformOrigin: {
                vertical: 'bottom',
                horizontal: 'left'
              } }}
              input={
                <InputBase id="select-multiple" 
                  classes={{
                    root: classes.inputRoot,
                    disabled: classes.disabled

                  }}
                />
              }
              renderValue={(selected: any) => getRenderValue(selected)}
              multiple={multiple}
              onFocus={(e: any) => {
                onFocus && onFocus(e);
                setSectionFocus(true)
              }}
              onBlur={(e: any) => {
                onBlur && onBlur(e);
                setSectionFocus(false)
              }}
              {...inputProps}
            >
              {children}
            </Select>
          )
        }

        {
          _.isEmpty(helperText) || (
            <FormHelperText>{helperText}</FormHelperText>
          )
        }
        {
          error && (
            <Typography className={classes.errorText} variant="caption" color="error"><Error />{errorText}</Typography>
          )
        }
      </div>
      )}
    </SectionFocusedContext.Consumer>
  );
};

//@ts-ignore
export default withStyles(styles)(MyPATSelect);